import merge from 'lodash/merge';
import { TFunction } from 'i18next';
import customConfig from 'src/config';

import { getDefaultAtpConfig, getTemplateConfig } from './cfg.default';
import cloneDeep from 'lodash/cloneDeep';

import {
  ActionEnum,
  ApplyBoundEnum,
  RiskAiFilter,
  RiskAiFilterRuleScope,
} from '@Policy/utils/types';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';

export const getDefaultRiskAiFilter = (
  t: TFunction<'translation'>,
  policyType: PolicyTypeEnum,
  featureShow: Record<string, boolean | number>,
  policyMode?: PolicyModeEnum
) => {
  const { gmailQtSettingFeatureShow } = featureShow;
  const isMonitor = [
    PolicyModeEnum.DefaultMonitorPolicy,
    PolicyModeEnum.DefaultXDRMonitorPolicy,
  ].includes(policyMode ?? PolicyModeEnum.DefaultPolicy);
  const isGmail = [PolicyTypeEnum.Gmail, PolicyTypeEnum.GmailInline].includes(
    policyType
  );
  return {
    tmRiskAiFilters: {
      raiEnabled: 1,
      raiAgenticAiEnabled: 0,
      raiActionConfirmed:
        isGmail && !gmailQtSettingFeatureShow
          ? ActionEnum.labelEmail
          : ActionEnum.quarantine,
      raiSuspiciousTagConfirmed: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiSuspiciousTagConfirmed'),
      raiConfirmedNotifyType: 0,
      raiActionSpam: isMonitor
        ? ActionEnum.pass
        : isGmail
          ? ActionEnum.moveToSpam
          : ActionEnum.moveToJunkEmailFolder,
      raiSuspiciousTagSpam: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiSubjectTagSpam'),
      raiSpamNotifyType: 0,
      raiSecurityRisksAddBannerEnabled: 0,
      raiAnomalyNotifyType: 0,
      raiAnomalyAddBannerEnable: 0,
      raiCustomNotifyType: 0,
      raiNotificationInstanceCfgsetting: '',
      tmRaiFilterRules: [],
      userDefinedExpressions: [],
      raiAnomalyRuleScope: RiskAiFilterRuleScope.AllRules,
      raiAnomalyExpRules: '',
      raiSuperAggActionSuspicious: ActionEnum.pass,
      raiSuperAggActionUnwanted: ActionEnum.pass,
      raiSuperAggSubjectTagSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiSuperAggSubjectTagUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiSuperAggDisclaimerSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiSuperAggDisclaimerUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiAggActionSuspicious: isMonitor
        ? ActionEnum.pass
        : isGmail
          ? ActionEnum.labelEmail
          : ActionEnum.tagSubject,
      raiAggActionUnwanted: isMonitor
        ? ActionEnum.pass
        : isGmail
          ? ActionEnum.labelEmail
          : ActionEnum.tagSubject,
      raiAggSubjectTagSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiAggSubjectTagUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiAggDisclaimerSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiAggDisclaimerUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiConsActionSuspicious: isMonitor
        ? ActionEnum.pass
        : isGmail
          ? ActionEnum.labelEmail
          : ActionEnum.tagSubject,
      raiConsActionUnwanted: isMonitor
        ? ActionEnum.pass
        : isGmail
          ? ActionEnum.labelEmail
          : ActionEnum.tagSubject,
      raiConsSubjectTagSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiConsSubjectTagUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'),
      raiConsDisclaimerSuspicious: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiConsDisclaimerUnwanted: isGmail
        ? ''
        : t('policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'),
      raiApprovedSenderEmailEnabled: 0,
      raiApprovedSenderEmailEntries: '',
      notification: {
        ntfAdminIntervalNum: 2,
        ntfAdminIntervalType: 0,
        ntfAdminMessage: t(
          'policy.policyTemplate.EX.tmRiskAiFilters.notification.ntfAdminMessage'
        ),
        ntfAdminNotifySettingType: 0,
        ntfAdminOccurenceNum: 50,
        ntfAdminSubject: t(
          'policy.policyTemplate.EX.tmRiskAiFilters.notification.ntfAdminSubject'
        ),
        ntfEnableEndUserRestore: 0,
        ntfDoNotNotifyExternalUser: 0,
        ntfEnableNotifyAdmin: 1,
        ntfEnableNotifyRecipient: 0,
        ntfInternalRecipientMessage: t(
          'policy.policyTemplate.EX.tmRiskAiFilters.notification.ntfInternalRecipientMessage'
        ),
        ntfInternalRecipientSubject: t(
          'policy.policyTemplate.EX.tmRiskAiFilters.notification.ntfInternalRecipientSubject'
        ),
        ntfToAccount: '',
        ntfRecipientGroupId: 0,
        ntfRecipientGroupEnabled: 1,
      },
    } as RiskAiFilter,
  };
};

function getExchangeConfig(
  t: TFunction<'translation'>,
  featureShow: Record<string, boolean | number>,
  policyMode?: PolicyModeEnum
) {
  return {
    policyType: 1,
    policySubType: 0,
    holderType: 1,
    tmWebthreatprotections: {
      wtpUrlcvEnabled: 1,
      wtpTocApprovedUrlListEnabled: 0,
      wtpTocApprovedUrlListEntries: '',
      wtpExtractAttachmentUrl: 1,
      wtpApplyBound: ApplyBoundEnum.all,
    },
    ...getDefaultRiskAiFilter(
      t,
      PolicyTypeEnum.Exchange,
      featureShow,
      policyMode
    ),
  };
}

export function getExchangeTemplateConfig(
  t: TFunction<'translation'>,
  featureShow: Record<string, boolean | number>,
  accountEmail: string | undefined,
  policyMode?: PolicyModeEnum
) {
  const coverI18nKeys = [
    'policy.policyTemplate.EX.tmAntispamfilters.asDisclaimerSuspiciousSender',
  ];
  const ntfI18nReplaceKeys = [
    'policy.policyTemplate.EX.tmFileblockings.notification.ntfAdminMessage',
    'policy.policyTemplate.EX.tmFileblockings.notification.ntfAdminSubject',
    'policy.policyTemplate.EX.tmFileblockings.notification.ntfInternalRecipientMessage',
    'policy.policyTemplate.EX.tmFileblockings.notification.ntfInternalRecipientSubject',
    'policy.policyTemplate.EX.tmVirusscans.notification.ntfAdminMessage',
    'policy.policyTemplate.EX.tmVirusscans.notification.ntfAdminSubject',
    'policy.policyTemplate.EX.tmVirusscans.notification.ntfInternalRecipientMessage',
    'policy.policyTemplate.EX.tmVirusscans.notification.ntfInternalRecipientSubject',
    'policy.policyTemplate.EX.tmWebthreatprotections.notification.ntfAdminMessage',
    'policy.policyTemplate.EX.tmWebthreatprotections.notification.ntfAdminSubject',
    'policy.policyTemplate.EX.tmWebthreatprotections.notification.ntfInternalRecipientMessage',
    'policy.policyTemplate.EX.tmWebthreatprotections.notification.ntfInternalRecipientSubject',
    'policy.policyTemplate.EX.tmDdafilters.notification.ntfAdminMessage',
    'policy.policyTemplate.EX.tmDdafilters.notification.ntfAdminSubject',
    'policy.policyTemplate.EX.tmDdafilters.notification.ntfInternalRecipientMessage',
    'policy.policyTemplate.EX.tmDdafilters.notification.ntfInternalRecipientSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfAdminMessage',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfAdminSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfInternalRecipientMessage',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfInternalRecipientSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleCompromisedUserSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleCompromisedUserMessage',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleExpectedSenderSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleExpectedSenderMessage',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleAdministratorSubject',
    'policy.policyTemplate.EX.tmAntispamfilters.notification.ntfWritingStyleAdministratorMessage',
  ];
  const tempConfig = getDefaultAtpConfig();
  merge(tempConfig, getExchangeConfig(t, featureShow, policyMode));
  return getTemplateConfig(
    t,
    tempConfig,
    accountEmail,
    coverI18nKeys,
    ntfI18nReplaceKeys
  );
}
export const convertTreeData = (treeData: any, t: TFunction<'translation'>) => {
  const result = treeData.map((item: any) => {
    if (item.children) {
      return {
        ...item,
        key: item.key.startsWith('policy.') ? t(item.key) : item.key,
        title: t(item.title),
        children: convertTreeData(item.children, t),
      };
    } else {
      return {
        ...item,
        key: item.key.startsWith('policy.') ? t(item.key) : item.key,
        title: t(item.title),
      };
    }
  });
  return result;
};
export function getAtpPolicyConfig(
  t: TFunction<'translation'>,
  currentOrgGlobalSetting: any,
  templateConfig: any
) {
  const originConfig: any = {
    limitation: cloneDeep(customConfig.ui.policy.limitation),
    fileTypes: cloneDeep(customConfig.ui.policy.fileTypes),
    featureShow: cloneDeep(customConfig.ui.policy.featureShow),
    validTokenList: cloneDeep(customConfig.ui.policy.validTokenList),
    defaultValue: {
      tmVirusscans: {
        vsReplacementText: templateConfig.tmVirusscans?.vsReplacementText,
        vsReplacementFileName:
          templateConfig.tmVirusscans?.vsReplacementFileName,
        vsReplacementTextUnscannable:
          templateConfig.tmVirusscans?.vsReplacementTextUnscannable,
        vsReplacementFileNameUnscannable:
          templateConfig.tmVirusscans?.vsReplacementFileNameUnscannable,
      },
      tmFileblockings: {
        fbReplacementText: templateConfig.tmFileblockings?.fbReplacementText,
        fbReplacementFileName:
          templateConfig.tmFileblockings?.fbReplacementFileName,
      },
      tmWebthreatprotections: {
        wtpReplacementText:
          templateConfig.tmWebthreatprotections?.wtpReplacementText,
      },
      tmDdafilters: {
        ddaReplacementText: templateConfig.tmDdafilters?.ddaReplacementText,
      },
    },
    exchangeOnlineApplyBoundOptions: [
      ApplyBoundEnum.incomming,
      ApplyBoundEnum.all,
    ],
  };

  originConfig.fileTypes = convertTreeData(customConfig.ui.policy.fileTypes, t);
  const config = {
    ...originConfig,
    limitation: {
      ...originConfig.limitation,
      blockedListMaxCount: currentOrgGlobalSetting?.blockSenderMaxCount
        ? parseInt(currentOrgGlobalSetting?.blockSenderMaxCount as string)
        : originConfig.limitation.blockedListMaxCount,
      blockedUrlMaxCount: currentOrgGlobalSetting?.blockUrlMaxCount
        ? parseInt(currentOrgGlobalSetting?.blockUrlMaxCount as string)
        : originConfig.limitation.blockedUrlMaxCount,
      approvedListMaxCount: originConfig.limitation.approvedListMaxCount,
      asApprovedSenderMaxCount: currentOrgGlobalSetting?.approvedSenderMaxCount
        ? parseInt(currentOrgGlobalSetting?.approvedSenderMaxCount as string)
        : originConfig.limitation.approvedListMaxCount,
    },
    featureShow: {
      ...originConfig.featureShow,
      urlSandboxFeatureShow: !!currentOrgGlobalSetting.urlSandboxFeatureShow,
      activeContentSanitizingExceptionListFeatureShow:
        !!currentOrgGlobalSetting?.activeContentSanitizingExceptionListFeatureShow,
      vaTempQuarantineEnabled:
        !!currentOrgGlobalSetting?.ddaTempQuarantineFeatureShow,
      aggressiveNameSpoofing: !!currentOrgGlobalSetting?.aggressiveNameSpoofing,
      gmailQtSettingFeatureShow:
        !!currentOrgGlobalSetting?.gmailQtSettingFeatureShow,
      spamActionSupportQt: !!currentOrgGlobalSetting?.spamActionSupportQt,
      passwordQuarantineFeatureShow:
        !!customConfig.ui.featureToggle.passwordQuarantineFeatureShow,
    },
  };
  console.log(config);
  return config;
}

/***********
 * 1. 所有的filter.notification
 * ntfEnableEndUserRestore, ntfDoNotNotifyExternalUser:
 * 这两个字段在老UI上，submit policy时不存在，但fetch policy时存在。不确定这两个字段ui是否会用
 * 2. tmAntispamfilters[0].notification
 * ntfWritingStyleAdministratorSubject, ntfWritingStyleAdministratorMessage:
 * 这两个字段，新UI没有，老UI有。老UI在submit的时候会提交这里两个字段，但是fetch policy时不存在这两个字段
 */
